import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Sun, Moon, Bell, LogOut, Shield, Building, Clock, LogIn, LogOut as LogOutIcon, MessageCircle, Users, ArrowLeftRight } from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import NotificationCenter from './NotificationCenter';

// Notification sounds using Web Audio API
const playNotificationSound = () => {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const playNote = (freq, start, dur) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.frequency.value = freq;
      osc.type = 'sine';
      gain.gain.setValueAtTime(0, ctx.currentTime + start);
      gain.gain.linearRampToValueAtTime(0.3, ctx.currentTime + start + 0.02);
      gain.gain.linearRampToValueAtTime(0, ctx.currentTime + start + dur);
      osc.start(ctx.currentTime + start);
      osc.stop(ctx.currentTime + start + dur);
    };
    playNote(784, 0, 0.12);
    playNote(1047, 0.1, 0.18);
  } catch (e) {
    console.log('Could not play sound');
  }
};

// Request browser notification permission
const requestNotificationPermission = async () => {
  if ('Notification' in window && Notification.permission === 'default') {
    await Notification.requestPermission();
  }
};

// Show browser notification (works even when tab is not active)
const showBrowserNotification = (title, body, onClick) => {
  if ('Notification' in window && Notification.permission === 'granted') {
    const notification = new Notification(title, {
      body: body,
      icon: '/favicon.ico',
      badge: '/favicon.ico',
      tag: 'ping-message-' + Date.now(),
      requireInteraction: false,
      silent: true, // We play our own sound
    });
    
    notification.onclick = () => {
      window.focus();
      if (onClick) onClick();
      notification.close();
    };
    
    // Auto close after 5 seconds
    setTimeout(() => notification.close(), 5000);
  }
};

const TopBar = ({ theme, onThemeToggle, currentUser, currentOffice }) => {
  const { logout, isSuperAdmin, isEmployee, isOfficeAdmin, canSwitchView, switchView, activeView, getAuthHeaders } = useAuth();
  const navigate = useNavigate();
  const [attendanceStatus, setAttendanceStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isInitialized, setIsInitialized] = useState(false);
  const [isPingEnabled, setIsPingEnabled] = useState(false);
  const notifiedMessagesRef = useRef(new Set());
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  // Request notification permission on mount
  useEffect(() => {
    requestNotificationPermission();
  }, []);

  // Check if Ping module is enabled for the current office
  useEffect(() => {
    const checkPingModule = async () => {
      const token = localStorage.getItem('auth_token');
      const currentOfficeData = localStorage.getItem('current_office');
      const officeId = currentOfficeData ? JSON.parse(currentOfficeData)?.id : null;

      if (!token || !officeId) {
        setIsPingEnabled(false);
        return;
      }

      try {
        const response = await fetch(`${API_URL}/api/modules/office/${officeId}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (response.ok) {
          const modules = await response.json();
          const hasPing = modules.some(m => m.name === 'Ping' || m.name === 'Ping (Chat)');
          setIsPingEnabled(hasPing);
        } else {
          setIsPingEnabled(false);
        }
      } catch {
        setIsPingEnabled(false);
      }
    };

    checkPingModule();
  }, [currentOffice, API_URL]);

  // Show toast notification for new message
  const showToastNotification = useCallback((senderName, message) => {
    toast(
      <div 
        className="flex items-center gap-3 cursor-pointer" 
        onClick={() => navigate('/ping')}
      >
        <div 
          className="w-10 h-10 rounded-full flex items-center justify-center text-white font-medium shrink-0"
          style={{ backgroundColor: '#25D366' }}
        >
          {senderName?.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase() || 'P'}
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-sm text-gray-900">{senderName || 'New Message'}</p>
          <p className="text-xs text-gray-500 truncate">{message}</p>
        </div>
      </div>,
      {
        duration: 5000,
        position: 'top-right',
        style: {
          background: 'white',
          border: '1px solid #e5e7eb',
          borderRadius: '12px',
          padding: '12px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
        },
      }
    );
  }, [navigate]);

  // Trigger notification for a new message
  const triggerNotification = useCallback((senderName, message) => {
    // Always play sound (works in foreground, may be blocked in background by browser)
    playNotificationSound();
    
    // Always show browser notification for reliability (works even when tab is hidden)
    // Browser notifications are more reliable for background tabs
    showBrowserNotification(
      `Ping from ${senderName}`,
      message,
      () => navigate('/ping')
    );
    
    // Show in-app toast only when tab is visible (avoid duplicate notifications)
    if (!document.hidden) {
      showToastNotification(senderName, message);
    }
  }, [showToastNotification, navigate]);

  // Fetch attendance status on mount
  useEffect(() => {
    fetchAttendanceStatus();
  }, []);

  // Fetch unread count and check for new messages (only when Ping is enabled)
  useEffect(() => {
    if (!isPingEnabled) {
      setUnreadCount(0);
      return;
    }

    const checkForNewMessages = async () => {
      try {
        const headers = getAuthHeaders();
        if (currentOffice?.id) headers['X-Office-ID'] = currentOffice.id;
        
        const response = await fetch(`${API_URL}/api/chat/conversations`, { headers });
        if (!response.ok) return;
        
        const conversations = await response.json();
        
        const totalUnread = conversations.reduce((sum, c) => sum + (c.unread_count || 0), 0);
        setUnreadCount(totalUnread);
        
        conversations.forEach(conv => {
          if (!conv.last_message || !conv.last_message_time) return;
          
          const messageKey = `${conv.id}:${conv.last_message_time}`;
          
          if (notifiedMessagesRef.current.has(messageKey)) return;
          if (!isInitialized) return;
          
          if (conv.unread_count > 0) {
            const senderName = conv.is_group 
              ? (conv.last_message_sender || conv.group_name)
              : conv.other_participant_name;
            
            triggerNotification(senderName, conv.last_message);
            notifiedMessagesRef.current.add(messageKey);
            
            if (notifiedMessagesRef.current.size > 100) {
              const arr = Array.from(notifiedMessagesRef.current);
              notifiedMessagesRef.current = new Set(arr.slice(-50));
            }
          }
        });
        
        if (!isInitialized) {
          conversations.forEach(conv => {
            if (conv.last_message_time) {
              notifiedMessagesRef.current.add(`${conv.id}:${conv.last_message_time}`);
            }
          });
          setIsInitialized(true);
        }
        
      } catch (error) {
        console.error('Error checking for new messages:', error);
      }
    };

    checkForNewMessages();
    const interval = setInterval(checkForNewMessages, 2500);
    return () => clearInterval(interval);
  }, [isPingEnabled, getAuthHeaders, currentOffice, API_URL, isInitialized, triggerNotification]);

  const fetchAttendanceStatus = async () => {
    try {
      const headers = getAuthHeaders();
      const response = await fetch(`${API_URL}/api/attendance/my-status`, { headers });
      if (response.ok) {
        const data = await response.json();
        setAttendanceStatus(data);
      }
    } catch (error) {
      console.error('Error fetching attendance status:', error);
    }
  };

  const handleCheckIn = async () => {
    setLoading(true);
    try {
      const headers = getAuthHeaders();
      const response = await fetch(`${API_URL}/api/attendance/check-in`, {
        method: 'POST',
        headers,
        body: JSON.stringify({})
      });
      if (response.ok) {
        fetchAttendanceStatus();
      } else {
        const error = await response.json();
        alert(error.detail || 'Failed to check in');
      }
    } catch (error) {
      console.error('Error checking in:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCheckOut = async () => {
    setLoading(true);
    try {
      const headers = getAuthHeaders();
      const response = await fetch(`${API_URL}/api/attendance/check-out`, {
        method: 'POST',
        headers,
        body: JSON.stringify({})
      });
      if (response.ok) {
        fetchAttendanceStatus();
      } else {
        const error = await response.json();
        alert(error.detail || 'Failed to check out');
      }
    } catch (error) {
      console.error('Error checking out:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
  };

  const handleSwitchView = () => {
    const newView = activeView === 'employee' ? 'admin' : 'employee';
    switchView(newView);
    navigate('/overview');
    toast.success(`Switched to ${newView === 'admin' ? 'Admin' : 'Employee'} view`);
  };

  const renderAttendanceButton = () => {
    if (!attendanceStatus || isSuperAdmin()) return null;

    const { status, check_in_time, total_hours, timezone } = attendanceStatus;

    if (status === 'not_checked_in') {
      return (
        <Button
          onClick={handleCheckIn}
          disabled={loading}
          className="bg-green-600 hover:bg-green-700 text-white"
          data-testid="check-in-button"
        >
          <LogIn className="w-4 h-4 mr-2" />
          Check In
        </Button>
      );
    }

    if (status === 'checked_in') {
      return (
        <div className="flex items-center gap-2">
          <div className="hidden sm:flex items-center gap-2 px-3 py-1.5 bg-green-50 dark:bg-green-950 rounded-lg">
            <Clock className="w-4 h-4 text-green-600" />
            <span className="text-sm font-medium text-green-900 dark:text-green-100">
              In: {check_in_time} {timezone && <span className="text-xs text-green-600">({timezone})</span>}
            </span>
          </div>
          <Button
            onClick={handleCheckOut}
            disabled={loading}
            variant="outline"
            className="border-red-300 text-red-600 hover:bg-red-50"
            data-testid="check-out-button"
          >
            <LogOutIcon className="w-4 h-4 mr-2" />
            Check Out
          </Button>
        </div>
      );
    }

    if (status === 'checked_out') {
      return (
        <div className="hidden sm:flex items-center gap-2 px-3 py-1.5 bg-gray-100 dark:bg-gray-800 rounded-lg">
          <Clock className="w-4 h-4 text-gray-600" />
          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
            Today: {total_hours}
          </span>
        </div>
      );
    }

    return null;
  };

  return (
    <div className="sticky top-0 z-40 backdrop-blur-sm bg-background/85 border-b border-border" data-testid="topbar">
      <div className="flex items-center justify-between h-16 px-4 sm:px-6">
        {/* Left side - Ping icon (only shown when Ping module is enabled) */}
        <div className="flex items-center gap-3">
          {isPingEnabled && (
            <button
              onClick={() => navigate('/ping')}
              className="relative flex items-center justify-center hover:scale-105 transition-transform"
              data-testid="ping-header-button"
            >
              <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{ backgroundColor: '#0F2E6E' }}>
                <MessageCircle className="w-7 h-7 text-white" fill="white" strokeWidth={0} />
              </div>
              {unreadCount > 0 && (
                <span className="absolute -top-1 -right-1 min-w-[22px] h-[22px] bg-red-500 text-white text-xs rounded-full flex items-center justify-center font-bold px-1 shadow-md border-2 border-white">
                  {unreadCount > 99 ? '99+' : unreadCount}
                </span>
              )}
            </button>
          )}
        </div>

        <div className="flex items-center gap-3">
          {/* Check In/Out Button */}
          {renderAttendanceButton()}

          {/* View Switch & Indicator for Office Admins */}
          {canSwitchView() && (
            <div className="flex items-center gap-1">
              {/* Current View Icon Indicator */}
              <div 
                className={`w-8 h-8 rounded-full flex items-center justify-center ${
                  activeView === 'employee'
                    ? 'bg-green-100 dark:bg-green-900'
                    : 'bg-purple-100 dark:bg-purple-900'
                }`}
                title={activeView === 'employee' ? 'Employee View' : 'Admin View'}
              >
                {activeView === 'employee' ? (
                  <Users className="w-4 h-4 text-green-600 dark:text-green-400" />
                ) : (
                  <Shield className="w-4 h-4 text-purple-600 dark:text-purple-400" />
                )}
              </div>
              
              {/* Switch Icon Button */}
              <Button
                variant="ghost"
                size="icon"
                onClick={handleSwitchView}
                className="hover:bg-gray-100 dark:hover:bg-gray-800"
                data-testid="switch-view-button"
                title={activeView === 'employee' ? 'Switch to Admin View' : 'Switch to Employee View'}
              >
                <ArrowLeftRight className="w-5 h-5 text-gray-600 dark:text-gray-400" />
              </Button>
            </div>
          )}

          {/* Office Name Badge */}
          {currentOffice && (
            <div className="hidden sm:flex items-center gap-2 px-3 py-1.5 bg-blue-50 dark:bg-blue-950 rounded-lg">
              <Building className="w-4 h-4 text-blue-600" />
              <span className="text-sm font-medium text-blue-900 dark:text-blue-100">
                {currentOffice.name}
              </span>
            </div>
          )}
          {isSuperAdmin() && !currentOffice && (
            <div className="hidden sm:flex items-center gap-2 px-3 py-1.5 bg-green-50 dark:bg-green-950 rounded-lg">
              <Shield className="w-4 h-4 text-green-600" />
              <span className="text-sm font-medium text-green-900 dark:text-green-100">
                Global Access
              </span>
            </div>
          )}

          {/* Theme Toggle */}
          <Button
            variant="ghost"
            size="icon"
            onClick={onThemeToggle}
            data-testid="theme-toggle"
          >
            {theme === 'light' ? <Moon className="w-5 h-5" /> : <Sun className="w-5 h-5" />}
          </Button>

          {/* Notifications */}
          <NotificationCenter />

          {/* User Menu */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="flex items-center gap-2 px-3" data-testid="user-menu-trigger">
                <div className="flex items-center gap-2">
                  <div className="hidden md:block text-right">
                    <p className="text-sm font-medium">{currentUser?.full_name}</p>
                    {isSuperAdmin() && (
                      <p className="text-xs text-muted-foreground">Super Admin</p>
                    )}
                  </div>
                  <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center">
                    <span className="text-white text-sm font-medium">
                      {currentUser?.first_name?.[0]}{currentUser?.last_name?.[0]}
                    </span>
                  </div>
                </div>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-64 bg-background" data-testid="user-menu-content">
              <div className="px-3 py-2">
                <p className="text-sm font-medium">{currentUser?.full_name}</p>
                <p className="text-xs text-muted-foreground">{currentUser?.phone}</p>
                {isSuperAdmin() && (
                  <Badge variant="secondary" className="text-xs mt-1">
                    <Shield className="w-3 h-3 mr-1" />
                    Super Admin
                  </Badge>
                )}
              </div>
              
              <DropdownMenuSeparator />
              
              <DropdownMenuItem onClick={handleLogout} data-testid="logout-menu-item" className="text-red-600">
                <LogOut className="w-4 h-4 mr-2" />
                Logout
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </div>
  );
};

export default TopBar;
